import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutIcon from "@mui/icons-material/Logout";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import {
  type Breakpoint,
  type SxProps,
  type Theme,
} from "@mui/material/styles";
import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import useLogout from "../../hooks/use-logout";
import { ROUTES } from "../../route-paths";
import EnvChip from "../crown-data-import/env/env-chip";
import { useCrownEnv } from "../crown-data-import/env/env-context";

const styles = {
  root: { display: "flex", flexDirection: "column", minHeight: "100vh" },
  header: {
    position: "sticky",
    top: 0,
    zIndex: (theme: Theme) => theme.zIndex.appBar,
    backgroundColor: "background.paper",
    borderBottom: 1,
    borderColor: "divider",
    display: "flex",
    alignItems: "center",
    gap: 2,
    px: 2,
    py: 1,
  },
  titles: { flex: 1, minWidth: 0 },
  // A column flex container rather than a block so a page whose body should
  // fill the space under the header (the Crown importer's full-height sidebar)
  // can claim it with `flex: 1`; for ordinary block content it renders the same.
  content: {
    flex: 1,
    minWidth: 0,
    py: 2,
    display: "flex",
    flexDirection: "column",
  },
} satisfies Record<string, SxProps<Theme>>;

/** Props for {@link PageLayout}. */
export type PageLayoutProps = {
  /** Page heading shown in the sticky header. */
  readonly title: string;
  /** Optional one-line explanation shown under the heading. */
  readonly subtitle?: string;
  /** Optional page-level controls rendered on the right of the header. */
  readonly actions?: ReactNode;
  /**
   * Content container width. Defaults to `"lg"`; pass `false` for pages whose
   * content needs the full viewport (canvas editors, wide dashboards).
   */
  readonly maxWidth?: Breakpoint | false;
  /** Drops the container's horizontal padding. */
  readonly disableGutters?: boolean;
  /** The page body. */
  readonly children: ReactNode;
};

/**
 * Shared chrome for every internal tool page.
 *
 * Gives each tool a sticky header with a back link to the launcher, its title
 * and optional subtitle, an actions slot, and a log out control — so no page is
 * a dead end that requires editing the URL to get out of.
 *
 * The header names the active target env. Every destructive action in this tool
 * (bulk import, config migration, piece backfill) runs against whichever env is
 * selected, and the picker only appears in the launcher and Crown importer
 * sidebars — so without this, an operator confirming a backfill on a tool page
 * had nothing on screen telling them whether it would land on demo or prod.
 */
const PageLayout = ({
  title,
  subtitle,
  actions,
  maxWidth = "lg",
  disableGutters = false,
  children,
}: PageLayoutProps) => {
  const navigate = useNavigate();
  const logout = useLogout();
  const { env } = useCrownEnv();

  return (
    <Box sx={styles.root}>
      <Box component="header" sx={styles.header}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            navigate(ROUTES.HOME);
          }}
        >
          All tools
        </Button>
        <Box sx={styles.titles}>
          <Typography noWrap variant="h6">
            {title}
          </Typography>
          {subtitle !== undefined && (
            <Typography noWrap variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <EnvChip env={env} />
          {actions}
          <Button
            size="small"
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={() => {
              void logout();
            }}
          >
            Log out
          </Button>
        </Stack>
      </Box>
      <Container
        disableGutters={disableGutters}
        maxWidth={maxWidth}
        sx={styles.content}
      >
        {children}
      </Container>
    </Box>
  );
};

export default PageLayout;
